/**
 * Email Verification API
 *
 * Mock email verification service.
 */

import type { ApiResponse, UUID } from '../types'
import { mockDb, delay, timestamp, generateId } from './mockDb'
import { success, error } from './client'

// Session key (shared)
const SESSION_KEY = 'docurious-session'
const VERIFICATION_KEY = 'docurious-email-verification'

interface VerificationToken {
  id: UUID
  userId: UUID
  email: string
  token: string
  expiresAt: string
  usedAt?: string
  createdAt: string
}

interface VerificationState {
  pendingEmail?: string
  tokenSentAt?: string
  verified: boolean
}

// Extended user type for verification status (not in main User type)
interface UserWithVerification {
  id: string
  email: string
  emailVerified?: boolean
  createdAt: string
  updatedAt: string
}

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

function getVerificationState(): VerificationState {
  const stored = localStorage.getItem(VERIFICATION_KEY)
  if (!stored) return { verified: false }
  try {
    return JSON.parse(stored)
  } catch {
    return { verified: false }
  }
}

function setVerificationState(state: VerificationState): void {
  localStorage.setItem(VERIFICATION_KEY, JSON.stringify(state))
}

/**
 * Email Verification API
 */
export const emailVerificationApi = {
  /**
   * Send verification email
   */
  async sendVerificationEmail(email: string): Promise<ApiResponse<{ sent: boolean; message: string }>> {
    await delay(500)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Generate verification token
    const token = generateId().replace(/-/g, '')
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours

    // Store token (in real app, this would be in the backend)
    const verificationToken: VerificationToken = {
      id: generateId(),
      userId,
      email,
      token,
      expiresAt,
      createdAt: timestamp(),
    }

    // Store in localStorage for demo
    const tokens = JSON.parse(localStorage.getItem('docurious-verification-tokens') || '[]')
    tokens.push(verificationToken)
    localStorage.setItem('docurious-verification-tokens', JSON.stringify(tokens))

    // Update verification state
    setVerificationState({
      pendingEmail: email,
      tokenSentAt: timestamp(),
      verified: false,
    })

    // Log the token for testing (in dev only)
    console.log('[Email Verification] Token:', token)
    console.log('[Email Verification] Verify URL:', `/verify-email?token=${token}`)

    return success({
      sent: true,
      message: `Verification email sent to ${email}. Check your inbox.`,
    })
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<ApiResponse<{ verified: boolean }>> {
    await delay(300)

    // Find token
    const tokens: VerificationToken[] = JSON.parse(
      localStorage.getItem('docurious-verification-tokens') || '[]'
    )
    const verificationToken = tokens.find(t => t.token === token && !t.usedAt)

    if (!verificationToken) {
      return error('Invalid or expired verification token')
    }

    // Check expiry
    if (new Date(verificationToken.expiresAt) < new Date()) {
      return error('Verification token has expired')
    }

    // Mark token as used
    const updatedTokens = tokens.map(t =>
      t.token === token ? { ...t, usedAt: timestamp() } : t
    )
    localStorage.setItem('docurious-verification-tokens', JSON.stringify(updatedTokens))

    // Update user's email verification status
    const user = mockDb.findById<UserWithVerification>('users', verificationToken.userId)
    if (user) {
      mockDb.update<UserWithVerification>('users', verificationToken.userId, {
        emailVerified: true,
        email: verificationToken.email,
      })
    }

    // Update verification state
    setVerificationState({
      verified: true,
    })

    return success({ verified: true })
  },

  /**
   * Resend verification email
   */
  async resendVerificationEmail(): Promise<ApiResponse<{ sent: boolean; message: string }>> {
    await delay(500)

    const state = getVerificationState()
    if (!state.pendingEmail) {
      return error('No pending email verification')
    }

    // Check rate limiting (1 email per minute)
    if (state.tokenSentAt) {
      const lastSent = new Date(state.tokenSentAt)
      const now = new Date()
      const diffSeconds = (now.getTime() - lastSent.getTime()) / 1000

      if (diffSeconds < 60) {
        return error(`Please wait ${Math.ceil(60 - diffSeconds)} seconds before requesting another email`)
      }
    }

    return this.sendVerificationEmail(state.pendingEmail)
  },

  /**
   * Get verification status
   */
  async getVerificationStatus(): Promise<ApiResponse<{
    isVerified: boolean
    pendingEmail?: string
    canResend: boolean
    resendWaitSeconds: number
  }>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const user = mockDb.findById<{ emailVerified?: boolean }>('users', userId)
    const state = getVerificationState()

    let canResend = false
    let resendWaitSeconds = 0

    if (state.tokenSentAt && !state.verified) {
      const lastSent = new Date(state.tokenSentAt)
      const now = new Date()
      const diffSeconds = (now.getTime() - lastSent.getTime()) / 1000

      if (diffSeconds >= 60) {
        canResend = true
      } else {
        resendWaitSeconds = Math.ceil(60 - diffSeconds)
      }
    } else {
      canResend = true
    }

    return success({
      isVerified: user?.emailVerified ?? state.verified,
      pendingEmail: state.pendingEmail,
      canResend,
      resendWaitSeconds,
    })
  },

  /**
   * Change email (requires re-verification)
   */
  async changeEmail(newEmail: string): Promise<ApiResponse<{ message: string }>> {
    await delay(200)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Basic email validation
    if (!newEmail.includes('@')) {
      return error('Invalid email address')
    }

    // Check if email is already in use
    const existingUser = mockDb.findOne<{ id: string; email: string }>(
      'users',
      u => u.email === newEmail && u.id !== userId
    )
    if (existingUser) {
      return error('Email is already in use')
    }

    // Send verification to new email
    await this.sendVerificationEmail(newEmail)

    return success({
      message: `Verification email sent to ${newEmail}. Please verify to complete the change.`,
    })
  },
}
